/**
 * Backfill the live-map rings from durable parquet when the user scrubs past what the session holds.
 *
 * The live stream only fills forward from the moment the page subscribed, and `liveColumnarStore`
 * has no read path older than its rings — so after an hour of streaming a paused map could still
 * only look back as far as the ring reached, and everything before that rendered as empty water.
 * Worse, a `reset` frame (reconnect / session boundary) DELETES a source's ring and restarts it
 * from a ~2 minute snapshot, so retained depth is not even monotonic.
 *
 * This closes the gap by reading the same parquet the batch pipeline reads, via
 * `POST /api/channel-values`, and merging the rows in with `liveColumnarStore.hydrateHistory`. There
 * is deliberately no second store: the rows land in the rings the GL layers already read, so a
 * hydrated span and a streamed span are indistinguishable downstream and nothing has to arbitrate
 * between two buffers.
 *
 * Runs while FOLLOWING as well as while paused. The original gate was paused-only, on the reasoning
 * that chasing the live edge never looks behind it — which is wrong as soon as the time-window dial
 * is widened: the window start is `playhead - window`, so a 30-minute window on a session holding
 * two minutes is asking for 28 minutes nobody has. Widening the window is in fact the most common
 * way to need this.
 */

import { createSignal } from 'solid-js';

import { apiEndpoints } from '@config/env';

import { LIVE_STREAM_CHANNELS } from '../constants/liveChannels';
import { postBinary } from '../utils/global';
import { debug, warn } from '../utils/console';
import { liveColumnarStore } from './liveColumnarStore';
import { persistantStore } from './persistantStore';

/**
 * Channels to backfill.
 *
 * EXACTLY the live-map subscription set (see LiveMapContainer's arrowStreamClient.subscribe), so a
 * hydrated boat renders identically to a streamed one. In particular this must NOT add `Cog_deg`:
 * it is absent from LIVE_STREAM_CHANNELS, so the boat sampler's COG branch is dead in live mode and
 * heading comes from the motion tangent — fetching it would make historical boats point differently
 * from live ones, with the change landing exactly at the seam.
 */
const HYDRATE_CHANNELS: readonly string[] = [
  ...LIVE_STREAM_CHANNELS,
  'Tws_sgp_kph',
  'Twd_sgp_deg',
];

const CHANNEL_LIST = HYDRATE_CHANNELS.map((name) => ({ name, type: 'float' }));

/**
 * Resolution asked of the parquet read.
 *
 * Deep history is context behind a boat, not a signal to analyse: 1 s is ample for a track line and
 * keeps an hour to ~3600 rows per boat instead of ~39,000 at the raw ~10.7 Hz. The live tail keeps
 * its full rate — the merge always prefers a retained sample over a fetched one at the same instant.
 */
const HYDRATE_RESOLUTION = '1s';

/** Don't re-ask for a span already covered, and don't spend a request on a sliver. */
const MIN_FETCH_SPAN_MS = 30_000;

/**
 * An interval between retained samples longer than this counts as a HOLE worth filling.
 *
 * Comfortably above any ordinary sampling gap (the feed runs ~10 Hz, and a brief GPS dropout is
 * seconds), so this only fires for a real outage — a socket that dropped and resumed. Without an
 * interior check, a ring spanning the whole window looks fully covered even when most of the middle
 * is missing, which is exactly what a reconnect leaves behind.
 */
const MAX_ACCEPTABLE_GAP_MS = 30_000;

/** Extra context pulled per request, so small scrubs don't each trigger a fetch. */
const OVERFETCH_MS = 10 * 60 * 1000;

/**
 * How long before a fruitless gap fetch is retried.
 *
 * A hole that parquet cannot fill (the boat genuinely was not logging) still reads as a hole
 * afterwards, so without a cooldown the gap check would re-ask on every tick forever.
 */
const GAP_RETRY_COOLDOWN_MS = 30_000;

/** In-flight request count, so the UI can say it is backfilling. */
const [hydratingCount, setHydratingCount] = createSignal(0);

/** True while at least one backfill request is outstanding. */
export const isHydratingHistory = (): boolean => hydratingCount() > 0;

/** Per-source guard: the span already asked for, and the request in flight. */
interface SourceState {
  /**
   * Oldest instant already requested for this source, or null if nothing yet.
   *
   * A frontier rather than a span: while FOLLOWING the playhead advances continuously, so a span
   * whose end had to be covered too would never match and every tick would re-request the same
   * history.
   */
  requestedFromMs: number | null;
  /** Wall clock of the last interior-gap fetch, for the cooldown above. */
  lastGapAttemptMs: number;
  inFlight: AbortController | null;
  /** Monotonic, so a late response for a superseded window is discarded. */
  seq: number;
}

const state = new Map<string, SourceState>();

/** Reset coverage — a session boundary invalidates every claim made about retained data. */
export function resetLiveMapHydration(): void {
  for (const s of state.values()) s.inFlight?.abort();
  state.clear();
  setHydratingCount(0);
}

function stateFor(name: string): SourceState {
  let s = state.get(name);
  if (!s) {
    s = { requestedFromMs: null, lastGapAttemptMs: 0, inFlight: null, seq: 0 };
    state.set(name, s);
  }
  return s;
}

/** `YYYYMMDD` in UTC — the partitioning the live writer uses (utcDayOfEpochSeconds). */
export function utcDateKey(ms: number): string {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

/** Every UTC day the span touches, oldest first. A scrub back can straddle midnight. */
export function utcDaysSpanning(startMs: number, endMs: number): string[] {
  const DAY = 86_400_000;
  const days: string[] = [];
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) return days;
  for (let t = Math.floor(startMs / DAY) * DAY; t <= endMs; t += DAY) days.push(utcDateKey(t));
  return days;
}

function numeric(row: Record<string, unknown>, key: string): number {
  const v = row[key] ?? row[key.toLowerCase()];
  if (v === undefined || v === null || v === '') return NaN;
  const n = typeof v === 'bigint' ? Number(v) : Number(v);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Parquet rows -> a columnar frame.
 *
 * Two conversions are load-bearing. `ts` is epoch SECONDS in parquet and epoch MILLISECONDS in the
 * rings. And a channel the row did not carry must become NaN, not 0 — `bracketFinite` and the boat
 * sampler both read NaN as "hole" and skip it, whereas a 0 in a position column is a real
 * coordinate in the Gulf of Guinea that the track would happily draw a line to.
 */
export function rowsToColumnarFrame(
  rows: Array<Record<string, unknown>>,
  frameSourceId: number,
  channels: readonly string[] = HYDRATE_CHANNELS
): { sourceIds: number[]; ts: number[]; channels: Record<string, number[]> } {
  const ts: number[] = [];
  const cols: Record<string, number[]> = {};
  for (const c of channels) cols[c] = [];

  for (const row of rows) {
    const tsRaw = numeric(row, 'ts');
    if (!Number.isFinite(tsRaw)) continue;
    // Seconds by contract; tolerate a millisecond value rather than landing the row in 1970.
    ts.push(tsRaw < 1e12 ? tsRaw * 1000 : tsRaw);
    for (const c of channels) cols[c].push(numeric(row, c));
  }

  return { sourceIds: ts.map(() => frameSourceId), ts, channels: cols };
}

export interface HydrateRequest {
  /** Boat name as the stream registry knows it. */
  sourceName: string;
  /** The stream-assigned ring id for that boat. */
  frameSourceId: number;
  /** Oldest instant the view wants to show. */
  windowStartMs: number;
  /** Newest instant the view wants to show (the paused playhead). */
  windowEndMs: number;
}

/**
 * Ensure the visible window is covered for one source, fetching only what has not been asked for.
 *
 * Fetches the WHOLE window rather than just the part preceding the ring. `hydrateHistory` merges,
 * so a single read repairs both the missing prefix and any interior gap left by a socket outage —
 * and at 1 s a 30 minute window is ~1800 rows, so asking for all of it costs less than the logic to
 * work out which slices are missing.
 *
 * @returns rows actually added to the ring (0 when nothing was needed, or nothing came back).
 */
export async function hydrateSourceBack(req: HydrateRequest): Promise<number> {
  const { sourceName, frameSourceId, windowStartMs, windowEndMs } = req;
  if (!Number.isFinite(windowStartMs) || !Number.isFinite(windowEndMs)) return 0;
  if (windowEndMs <= windowStartMs) return 0;

  const className = persistantStore.selectedClassName?.();
  const projectId = persistantStore.selectedProjectId?.();
  if (!className || !projectId) {
    debug('[liveMapHydration] no class/project selected; skipping');
    return 0;
  }

  // A source with no ring at all is not being streamed this session; there is nothing to extend,
  // and the caller has already skipped anything it could not resolve in the registry.
  const ringOldest = liveColumnarStore.oldestTsFor(frameSourceId);
  if (ringOldest == null) return 0;

  const s = stateFor(sourceName);
  const now = Date.now();

  // Two distinct reasons to fetch, and BOTH must be tested. Checking only the first reports a ring
  // that straddles an outage as fully covered; checking only the second misses the ordinary case of
  // a window reaching back further than the session has been running.
  const missingPrefix = windowStartMs < ringOldest - MIN_FETCH_SPAN_MS;
  const hasGap =
    liveColumnarStore.maxGapWithin(frameSourceId, windowStartMs, windowEndMs) > MAX_ACCEPTABLE_GAP_MS;
  const gapRetryDue = now - s.lastGapAttemptMs > GAP_RETRY_COOLDOWN_MS;
  const fillGap = hasGap && gapRetryDue;

  if (!missingPrefix && !fillGap) return 0;

  const startMs = windowStartMs - OVERFETCH_MS;
  // Prefix-only stops at the ring's own oldest sample: the stream owns everything above it, and
  // asking for that range too would overlap the full-rate tail with decimated rows for nothing.
  const endMs = fillGap ? windowEndMs : ringOldest - 1;
  if (endMs <= startMs) return 0;

  // Already asked back at least this far, and nothing new is torn? Then this is free.
  if (!fillGap && s.requestedFromMs != null && startMs >= s.requestedFromMs - MIN_FETCH_SPAN_MS) {
    return 0;
  }

  // Supersede any in-flight request for this boat: the newer window is the one being looked at.
  s.inFlight?.abort();
  const controller = new AbortController();
  s.inFlight = controller;
  const mySeq = ++s.seq;
  // Claim the span up front so a concurrent scrub does not ask for it a second time.
  const previousClaim = s.requestedFromMs;
  s.requestedFromMs = s.requestedFromMs == null ? startMs : Math.min(s.requestedFromMs, startMs);
  // Every request resets the gap cooldown, not just a gap-driven one: the read just established
  // what parquet holds for this region, so a hole still showing afterwards is a hole parquet cannot
  // fill. Without this, a sparse backfill leaves a seam that immediately reads as a new gap and
  // asks again on the very next tick.
  s.lastGapAttemptMs = now;

  let total = 0;
  setHydratingCount((n) => n + 1);
  try {
    for (const date of utcDaysSpanning(startMs, endMs)) {
      const res = await postBinary(
        apiEndpoints.file.channelValues,
        {
          project_id: projectId,
          class_name: className,
          date,
          source_name: sourceName,
          channel_list: CHANNEL_LIST,
          start_ts: Math.floor(startMs / 1000),
          // FLOOR, not ceil: parquet `ts` is in seconds, and rounding up would re-request the very
          // second the ring already owns at full rate — the one row the merge would then discard.
          end_ts: Math.floor(endMs / 1000),
          resolution: HYDRATE_RESOLUTION,
          data_source: 'file',
          timezone: 'UTC',
        },
        controller.signal
      );

      if (mySeq !== s.seq || controller.signal.aborted) return total;

      if (!res.success) {
        // 404 = no parquet for that boat on that day, which is ordinary (it was not sailing).
        if (res.status !== 404) {
          warn('[liveMapHydration] channel-values failed', { sourceName, date, status: res.status });
        }
        continue;
      }

      const rows = Array.isArray(res.data) ? (res.data as Array<Record<string, unknown>>) : [];
      if (rows.length === 0) continue;

      total += liveColumnarStore.hydrateHistory(frameSourceId, rowsToColumnarFrame(rows, frameSourceId));
    }
  } catch (err) {
    if (!controller.signal.aborted) {
      warn('[liveMapHydration] hydrate failed', { sourceName, err });
    }
    // Release the claim rather than latching a span that was never filled.
    if (mySeq === s.seq) s.requestedFromMs = previousClaim;
  } finally {
    setHydratingCount((n) => Math.max(0, n - 1));
    if (s.inFlight === controller) s.inFlight = null;
  }

  if (total > 0) {
    debug('[liveMapHydration] hydrated', { sourceName, rows: total, startMs, endMs });
  }
  return total;
}
